import { signedTon, tartiNoText } from '@/features/report/reconcile/format'
import { Section, SimpleTable, StatusBadge } from '@/features/report/reconcile/parts'
import { useIsMobile } from '@/hooks/use-mobile'
import { formatInteger } from '@/lib/format'
import {
  RECORD_STATUSES,
  formatTon,
  type KantarSummary,
  type ReconcileResult,
} from '@/lib/kantar-dia'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

const t = messages.reconcile
const col = t.columns

function KantarCard({ summary }: { summary: KantarSummary }) {
  const { tonnage } = summary
  return (
    <div className="border-border bg-surface rounded-surface shadow-resting flex flex-col gap-2 border p-4 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{summary.kantarAdi}</span>
        <span className="text-muted-foreground tabular">
          {t.tonnage.count(formatInteger(summary.total))}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {RECORD_STATUSES.filter((status) => summary.byStatus[status] > 0).map((status) => (
          <span key={status} className="flex items-center gap-1">
            <StatusBadge status={status} />
            <span className="tabular">{formatInteger(summary.byStatus[status])}</span>
          </span>
        ))}
      </div>
      <span className="tabular text-xs">
        {t.mobile.tonnage(
          `${formatTon(tonnage.kantarKg)} t`,
          `${formatTon(tonnage.diaKg)} t`,
          `${signedTon(tonnage.diffKg)} t`,
        )}
      </span>
    </div>
  )
}

/** Kantar bazında durum dağılımı ve tonaj (yalnız özet; tıklanacak bir şey yok). */
export function KantarsTab({
  summaries,
  result,
}: {
  summaries: KantarSummary[]
  result: ReconcileResult
}) {
  const isMobile = useIsMobile()
  return (
    <div className="flex flex-col gap-4">
      {isMobile ? (
        <div className="flex flex-col gap-2">
          {summaries.map((summary) => (
            <KantarCard key={summary.kantarAdi} summary={summary} />
          ))}
        </div>
      ) : (
        <Section title={t.tabs.kantars}>
          <SimpleTable
            head={[
              col.kantar,
              col.tartiCount,
              ...RECORD_STATUSES.map((status) => t.status[status]),
              t.tonnage.kantar,
              t.tonnage.dia,
              t.tonnage.diff,
            ]}
            numeric={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
            rows={summaries.map((summary) => [
              summary.kantarAdi,
              formatInteger(summary.total),
              ...RECORD_STATUSES.map((status) => formatInteger(summary.byStatus[status])),
              formatTon(summary.tonnage.kantarKg),
              formatTon(summary.tonnage.diaKg),
              <span className={cn(summary.tonnage.diffKg !== 0 && 'text-destructive')}>
                {signedTon(summary.tonnage.diffKg)}
              </span>,
            ])}
          />
        </Section>
      )}

      {(result.prefixMismatches.length > 0 || result.unknownPrefixes.length > 0) && (
        <Section title={t.prefixTitle}>
          {result.unknownPrefixes.map((item) => (
            <p key={item.kantarAdi} className="text-muted-foreground px-4 py-2 text-sm">
              {t.prefixUnknown(item.kantarAdi, item.values.join(', '))}
            </p>
          ))}
          {result.prefixMismatches.length > 0 && (
            <SimpleTable
              head={[col.kantar, col.tartino, col.expected, col.actual]}
              rows={result.prefixMismatches.map((item) => [
                item.kantarAdi,
                tartiNoText(item.tartino),
                item.expected,
                item.actual,
              ])}
            />
          )}
        </Section>
      )}
    </div>
  )
}
